import type {
  LegacyProjectReference,
  LegacyProjectReferenceWriteEnvelope,
  ProjectReference,
} from '@indutrust/factory-model';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * True when the payload already looks like a domain {@link ProjectReference}.
 */
export function isDomainProjectReference(
  wire: unknown
): wire is ProjectReference {
  return (
    isRecord(wire) &&
    typeof wire['description'] === 'string' &&
    Array.isArray(wire['capability_ids']) &&
    Array.isArray(wire['industrial_subtype_ids'])
  );
}

/**
 * Maps a legacy project-reference payload to {@link ProjectReference}.
 */
export function legacyToProjectReference(wire: unknown): ProjectReference {
  if (isDomainProjectReference(wire)) {
    return wire;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- legacy wire
  const legacy = wire as LegacyProjectReference;
  return {
    id: legacy.id,
    factory_id: legacy.factory_id,
    description: legacy.description,
    customer_name: legacy.customer_name,
    country_id: legacy.country_id ?? null,
    start_date: legacy.start_date,
    end_date: legacy.end_date,
    industrial_subtype_ids: legacy.industrial_subtype_ids ?? [],
    capability_ids: legacy.capability_ids ?? [],
    value: legacy.value,
    on_demand: legacy.on_demand,
  };
}

/**
 * Builds the legacy `{ project_reference: … }` write envelope.
 */
export function projectReferenceToLegacy(
  entity: Partial<ProjectReference>
): LegacyProjectReferenceWriteEnvelope {
  const projectReference: Record<string, unknown> = { ...entity };
  delete projectReference['id'];
  delete projectReference['factory_id'];
  return { project_reference: projectReference };
}
